import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const STORAGE_KEY = "botmoileqi_cookie_consent";
const CHOICE_ACCEPTED = "accepted";
const CHOICE_DECLINED = "declined";

/**
 * RGPD cookie consent banner.
 *
 * PostHog is initialised with `opt_out_capturing_by_default: true`. When the
 * user accepts, we call `posthog.opt_in_capturing()` so analytics start. When
 * they decline, we explicitly opt them out (no-op since default is opt-out).
 *
 * The choice is persisted in localStorage so the banner doesn't show again.
 */
const CookieConsent = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        try {
            const choice = localStorage.getItem(STORAGE_KEY);
            if (!choice) {
                // Show after a short delay so the hero loads first.
                const t = setTimeout(() => setVisible(true), 1500);
                return () => clearTimeout(t);
            }
            // Re-apply the prior choice on every page load (in case the user
            // cleared a session cookie but kept localStorage).
            if (choice === CHOICE_ACCEPTED && window.posthog?.opt_in_capturing) {
                window.posthog.opt_in_capturing();
            }
        } catch {
            // localStorage might be disabled — show the banner anyway.
            setVisible(true);
        }
    }, []);

    const accept = () => {
        try {
            localStorage.setItem(STORAGE_KEY, CHOICE_ACCEPTED);
        } catch {}
        if (window.posthog?.opt_in_capturing) {
            window.posthog.opt_in_capturing();
            // Fire the first pageview now that consent is granted.
            window.posthog.capture?.("$pageview");
        }
        setVisible(false);
    };

    const decline = () => {
        try {
            localStorage.setItem(STORAGE_KEY, CHOICE_DECLINED);
        } catch {}
        if (window.posthog?.opt_out_capturing) {
            window.posthog.opt_out_capturing();
        }
        setVisible(false);
    };

    if (!visible) return null;

    // Petit bandeau discret : en bas à gauche sur ordinateur (jamais devant le champ e-mail
    // ni le bouton du haut de l'accueil), pleine largeur mais d'une seule ligne de boutons
    // sur téléphone. Accepter et Refuser ont le même poids (RGPD). Style : carte « La carte du jour ».
    return (
        <div
            data-testid="cookie-consent"
            role="dialog"
            aria-label="Bandeau de consentement aux cookies"
            className="cj-cookie"
        >
            <button
                onClick={decline}
                aria-label="Refuser et fermer"
                data-testid="cookie-close"
                className="cj-cookie-x"
            >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
            <p className="cj-cookie-txt">
                Mesure d&apos;audience anonyme (PostHog), aucune publicité, aucun traceur tiers.{" "}
                <Link to="/confidentialite">En savoir plus</Link>
            </p>
            <div className="cj-cookie-btns">
                <button onClick={accept} data-testid="cookie-accept" className="cj-cookie-ok">
                    Accepter
                </button>
                <button onClick={decline} data-testid="cookie-decline" className="cj-cookie-non">
                    Refuser
                </button>
            </div>
        </div>
    );
};

export default CookieConsent;
